<script lang="ts">
  /**
   * Saldo diário (hora extra - déficit) com cores divergentes:
   * verde acima do zero = colaboradores acumulando saldo positivo,
   * vermelho abaixo = empresa tem déficit. Linha do zero destacada.
   */
  import type { DailyAnalyticsPoint } from '$core/metrics/metrics.types';
  import {
    makeGeometry,
    formatDateShort,
    formatMinutesCompact,
    niceMax,
    thinLabels,
  } from './chartUtils';

  export let daily: DailyAnalyticsPoint[];
  export let width: number = 720;
  export let height: number = 220;

  const geom = makeGeometry(width, height, { left: 56, bottom: 32 });
  const TICKS = [-1, -0.5, 0, 0.5, 1];

  // Pré-computa toda a geometria num único bloco reativo. Garante que bars,
  // grid e zero usem a mesma escala (maxAbs) e que Svelte recalcule tudo
  // junto quando `daily` muda.
  $: model = (() => {
    const maxAbs = niceMax(
      Math.max(1, ...daily.map(d => Math.abs(d.balanceMinutes))),
    );
    const stepX = daily.length > 1 ? geom.innerWidth / (daily.length - 1) : geom.innerWidth;
    const bandWidth = Math.max(2, daily.length > 0 ? geom.innerWidth / daily.length * 0.78 : 0);
    const baseY = geom.padding.top + geom.innerHeight;
    const toX = (i: number) => daily.length <= 1
      ? geom.padding.left + geom.innerWidth / 2
      : geom.padding.left + i * stepX;
    const toY = (value: number) => {
      const ratio = (value + maxAbs) / (2 * maxAbs);
      return baseY - ratio * geom.innerHeight;
    };
    const zeroY = toY(0);
    const bars = daily.map((d, i) => {
      const xCenter = toX(i);
      const yVal = toY(d.balanceMinutes);
      const barTop = Math.min(zeroY, yVal);
      const barH = Math.abs(zeroY - yVal);
      return {
        date: d.date,
        x: xCenter - bandWidth / 2,
        y: barTop,
        width: bandWidth,
        height: Math.max(barH, d.balanceMinutes === 0 ? 0 : 1),
        fill: d.balanceMinutes >= 0 ? '#2CE057' : '#FB7224',
        opacity: d.balanceMinutes === 0 ? 0 : 0.9,
        labelX: xCenter,
      };
    });
    const gridlines = TICKS.map(t => ({
      t,
      y: toY(maxAbs * t),
      label: t === 0 ? '0' : (t > 0 ? '+' : '') + formatMinutesCompact(maxAbs * t),
    }));
    return { maxAbs, bandWidth, zeroY, bars, gridlines };
  })();

  $: totalBalance = daily.reduce((acc, d) => acc + d.balanceMinutes, 0);
  $: overtimeTotal = daily.reduce((acc, d) => acc + d.overtimeMinutes, 0);
  $: deficitTotal = daily.reduce((acc, d) => acc + d.deficitMinutes, 0);
  $: showLabel = thinLabels(daily, 8);
</script>

<div class="chart">
  <header class="chart__head">
    <div>
      <h3 class="chart__title">Saldo de jornada por dia</h3>
      <p class="chart__subtitle">Hora extra menos déficit, agregado por dia</p>
    </div>
    <div class="chart__kpis">
      <div class="kpi">
        <span class="kpi__label">Saldo do período</span>
        <span class="kpi__value" class:kpi__value--positive={totalBalance >= 0} class:kpi__value--negative={totalBalance < 0}>
          {totalBalance >= 0 ? '+' : ''}{formatMinutesCompact(totalBalance)}
        </span>
      </div>
      <div class="kpi">
        <span class="kpi__label">Hora extra</span>
        <span class="kpi__value">{formatMinutesCompact(overtimeTotal)}</span>
      </div>
      <div class="kpi">
        <span class="kpi__label">Déficit</span>
        <span class="kpi__value">{formatMinutesCompact(deficitTotal)}</span>
      </div>
    </div>
  </header>

  <svg viewBox="0 0 {width} {height}" preserveAspectRatio="none" role="img" aria-label="Saldo de jornada por dia">
    {#each model.gridlines as g}
      <line
        x1={geom.padding.left}
        x2={geom.padding.left + geom.innerWidth}
        y1={g.y}
        y2={g.y}
        stroke={g.t === 0 ? '#7D7D7D' : '#E7E7E7'}
        stroke-dasharray={g.t === 0 ? '0' : '2 4'}
        stroke-width={g.t === 0 ? '1.4' : '1'}
      />
      <text
        x={geom.padding.left - 8}
        y={g.y + 4}
        font-size="10"
        text-anchor="end"
        fill="#7D7D7D"
      >{g.label}</text>
    {/each}

    {#each model.bars as bar}
      <rect
        x={bar.x}
        y={bar.y}
        width={bar.width}
        height={bar.height}
        fill={bar.fill}
        opacity={bar.opacity}
        rx="1.5"
      />
    {/each}

    {#each daily as d, i}
      {#if showLabel[i]}
        <text
          x={model.bars[i]?.labelX ?? 0}
          y={geom.padding.top + geom.innerHeight + 16}
          font-size="10"
          text-anchor="middle"
          fill="#565656"
        >{formatDateShort(d.date)}</text>
      {/if}
    {/each}
  </svg>

  <ul class="legend" aria-label="Legenda">
    <li><span class="legend__swatch" style="background: #2CE057"></span>Saldo positivo (hora extra acumulada)</li>
    <li><span class="legend__swatch" style="background: #FB7224"></span>Saldo negativo (déficit)</li>
  </ul>
</div>

<style>
  .chart { display: flex; flex-direction: column; gap: var(--ptt-space-3); }
  .chart__head { display: flex; justify-content: space-between; gap: var(--ptt-space-4); flex-wrap: wrap; align-items: flex-end; }
  .chart__title { margin: 0; font-size: 1rem; font-weight: 700; color: var(--ptt-gray-300); }
  .chart__subtitle { margin: 2px 0 0; font-size: 0.8125rem; color: var(--ptt-gray-100); }
  .chart__kpis { display: flex; gap: var(--ptt-space-4); flex-wrap: wrap; }
  .kpi { display: flex; flex-direction: column; line-height: 1.1; }
  .kpi__label { font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--ptt-gray-100); }
  .kpi__value { font-size: 1.125rem; font-weight: 700; color: var(--ptt-gray-300); margin-top: 2px; }
  .kpi__value--positive { color: #1da244; }
  .kpi__value--negative { color: var(--ptt-orange-100); }
  svg { width: 100%; height: auto; display: block; }
  .legend { list-style: none; margin: 0; padding: 0; display: flex; gap: var(--ptt-space-4); flex-wrap: wrap; font-size: 0.75rem; color: var(--ptt-gray-200); }
  .legend li { display: inline-flex; align-items: center; gap: var(--ptt-space-2); }
  .legend__swatch { width: 12px; height: 12px; border-radius: 3px; display: inline-block; }
</style>
